/*
 * Contrato de cliente ObtenerTemaPreferido / EstablecerTema (D-08, RF-032).
 *
 * ES UN CONTRATO DE CLIENTE, NO DE API: no existe endpoint, el tema no viaja al
 * servidor y no hay columna en base de datos. Existe para que ningun modulo lea
 * localStorage por su cuenta ni consulte prefers-color-scheme a mano.
 *
 * Ambito: POR DISPOSITIVO. El telefono puede quedarse en oscuro y la computadora
 * en claro; dos personas que comparten un aparato comparten su tema (RN-045d).
 * Cerrar sesion NO borra la preferencia: no es dato de negocio (RF-027).
 */

export const CLAVE_DE_ALMACENAMIENTO = 'gp.tema';

export const MODOS = Object.freeze({
  CLARO: 'CLARO',
  OSCURO: 'OSCURO',
  AUTOMATICO: 'AUTOMATICO',
});

const MODOS_VALIDOS = [MODOS.CLARO, MODOS.OSCURO, MODOS.AUTOMATICO];

// Respaldo en memoria para navegadores en modo privado restrictivo: el tema
// funciona durante la sesion y se pierde al cerrar, sin mostrar error.
let modoEnMemoria = null;
const suscriptores = new Set();

function leerModoGuardado() {
  try {
    const guardado = localStorage.getItem(CLAVE_DE_ALMACENAMIENTO);
    if (MODOS_VALIDOS.includes(guardado)) return guardado;
  } catch {
    /* almacenamiento no disponible */
  }
  return MODOS_VALIDOS.includes(modoEnMemoria) ? modoEnMemoria : MODOS.AUTOMATICO;
}

/** Preferencia del sistema operativo. Si el navegador no la expone, CLARO (RN-045e). */
export function temaDelSistema() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return MODOS.CLARO;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? MODOS.OSCURO : MODOS.CLARO;
  } catch {
    return MODOS.CLARO;
  }
}

/**
 * Devuelve { modo, temaEfectivo, origen }.
 *   modo: lo que la persona eligio en este dispositivo. Por omision AUTOMATICO.
 *   temaEfectivo: CLARO u OSCURO, lo que realmente se pinta.
 *   origen: ELEGIDO o SISTEMA; alimenta la leyenda de Ajustes y el diagnostico.
 */
export function obtenerTemaPreferido() {
  const modo = leerModoGuardado();
  const esAutomatico = modo === MODOS.AUTOMATICO;
  return {
    modo,
    temaEfectivo: esAutomatico ? temaDelSistema() : modo,
    origen: esAutomatico ? 'SISTEMA' : 'ELEGIDO',
  };
}

function aplicarAlDocumento(preferencia) {
  if (typeof document === 'undefined') return;
  const raiz = document.documentElement;
  raiz.setAttribute('data-modo', preferencia.modo);
  raiz.setAttribute('data-tema', preferencia.temaEfectivo === MODOS.OSCURO ? 'oscuro' : 'claro');

  // La etiqueta theme-color y el fondo de la pantalla de inicio siguen al tema
  // en uso (RF-017, RN-045c).
  const etiqueta = document.getElementById('colorDeTema');
  if (etiqueta) {
    etiqueta.setAttribute('content', preferencia.temaEfectivo === MODOS.OSCURO ? '#101a2b' : '#0e2f6d');
  }
}

function avisar() {
  const preferencia = obtenerTemaPreferido();
  aplicarAlDocumento(preferencia);
  suscriptores.forEach((llamada) => llamada(preferencia));
}

/** Solo lo invoca Ajustes › Apariencia. Un valor invalido se ignora y se conserva el anterior. */
export function establecerTema(modo) {
  if (!MODOS_VALIDOS.includes(modo)) return obtenerTemaPreferido();

  modoEnMemoria = modo;
  try {
    localStorage.setItem(CLAVE_DE_ALMACENAMIENTO, modo);
  } catch {
    /* queda solo en memoria */
  }

  avisar();
  return obtenerTemaPreferido();
}

/** Suscripcion para componentes que recalculan colores (por ejemplo, graficas). */
export function suscribirseAlTema(llamada) {
  suscriptores.add(llamada);
  return () => suscriptores.delete(llamada);
}

/**
 * Escucha del sistema operativo: en modo AUTOMATICO la aplicacion cambia en vivo,
 * sin recargar y sin perder lo que se este capturando (RN-045a).
 */
export function iniciarEscuchaDelSistema() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};

  let consulta;
  try {
    consulta = window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return () => {};
  }

  const alCambiar = () => {
    if (obtenerTemaPreferido().modo === MODOS.AUTOMATICO) avisar();
  };

  if (consulta.addEventListener) consulta.addEventListener('change', alCambiar);
  else if (consulta.addListener) consulta.addListener(alCambiar);

  return () => {
    if (consulta.removeEventListener) consulta.removeEventListener('change', alCambiar);
    else if (consulta.removeListener) consulta.removeListener(alCambiar);
  };
}

/** Aplica el tema guardado al documento. El primer pintado ya lo hizo index.html. */
export function aplicarTemaGuardado() {
  aplicarAlDocumento(obtenerTemaPreferido());
}
